import './index.css'
import './styles/_global.scss'
import 'assets/fonts/SF-Pro-Display-Regular.ttf'

import { useEffect } from 'react'
import { createRoot } from 'react-dom/client'

import { GoogleOAuthProvider } from '@react-oauth/google'
import * as Sentry from "@sentry/react";
import { Toaster } from 'react-hot-toast'
import { I18nextProvider } from 'react-i18next'
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'

// import { ReactQueryDevtools } from 'react-query/devtools'
import { GoogleAnalyticsTracker } from 'components/GoogleAnalyticsTracker/GoogleAnalyticsTracker'
import { HeaderLayoutProvider } from 'context/headerLayoutContext'
import { HeaderNavProvider } from 'context/headerNavContext'
import { HydrationGate } from 'context/HydrationGate'
import { PageTitleProvider } from 'context/pageTitleContext';
import { SessionTransitionGate } from 'features/Auth/SessionTransitionGate/SessionTransitionGate'
import { AuthProvider, ReactQueryProvider } from 'providers'
import GlobalStyle from 'styles/GlobalStyles'

import { GOOGLE_LOGIN_CLIENT_ID } from './constants/constants'
import { GlobalVideoProvider } from './context/globalVideoContext'
import { MobileProvider } from './context/isMobile';
import { LayoutProvider } from './context/layoutContext';
import { LoadingProvider } from './context/loadingContext'
import { ModalProvider } from './context/modalContext'
import { PreviewProvider } from './context/previewContext'
import { ProfileMarkerProvider } from './context/profileContext'
import { TrackingProvider } from './context/trackingContext'
import { UserProvider } from './context/userContext'
import Router from './routes'
import ScrollManager from './ScrollManager';
import i18n from './services/i18n/i18n'

const root = createRoot(document.getElementById('root') as HTMLElement)

const appEnv = process.env.REACT_APP_ENV; // "local" | "production"
const isProd = appEnv === "production";

if (isProd) {
    Sentry.init({
    dsn: "https://0fb7ea820baa23a237d2170214e8d5f5@o4510760674197504.ingest.us.sentry.io/4510760759001088",

    sendDefaultPii: false,

    // errors always work regardless of tracesSampleRate
    // minimal/no perf in prod
    tracesSampleRate: isProd ? 0.005 : 0.1, // 0.5% in prod, 10% in dev

    // Optional: kill the "client_report" noise
    sendClientReports: false,

    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        }),
    ],

    tracePropagationTargets: [
        // dev
        /^http:\/\/localhost(:\d+)?\/api\//,
        /^http:\/\/127\.0\.0\.1(:\d+)?\/api\//,
        /^http:\/\/api-dev\.allfans\.com:8000\/api\//,

        // prod
        /^https:\/\/api\.allfans\.com\/api\//,
        /^https:\/\/allfans\.com\/api\//,
    ],
    });
}
root.render(
     /* <React.StrictMode> */
        <ReactQueryProvider>
          <PageTitleProvider>
            <HeaderLayoutProvider>
            <LayoutProvider>
            <HeaderNavProvider>
            <BrowserRouter>
            <GoogleAnalyticsTracker />
            <SessionTransitionGate>
              <HydrationGate>
                <ScrollManager>
                    <UserProvider>
                        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
                        <TrackingProvider>
                          <GlobalVideoProvider>
                            <ModalProvider>
                                <PreviewProvider>
                                    <ProfileMarkerProvider>
                                        <LoadingProvider>
                                            <MobileProvider>
                                            <I18nextProvider i18n={i18n}>
                                                <GoogleOAuthProvider
                                                    clientId={
                                                        GOOGLE_LOGIN_CLIENT_ID
                                                    }
                                                >
                                                    <AuthProvider>
                                                        <Router />
                                                        <GlobalStyle />
                                                    </AuthProvider>
                                                    <Toaster
                                                        position='top-center'
                                                        reverseOrder={false}
                                                        containerStyle={{
                                                            top: 0,
                                                            left: 0,
                                                            right: 0
                                                        }}
                                                    />
                                                </GoogleOAuthProvider>
                                            </I18nextProvider>
                                            </MobileProvider>
                                        </LoadingProvider>
                                    </ProfileMarkerProvider>
                                </PreviewProvider>
                            </ModalProvider>
                           </GlobalVideoProvider>
                        </TrackingProvider>
                    </UserProvider>
                </ScrollManager>
                </HydrationGate>
             </SessionTransitionGate>   
            </BrowserRouter>
            </HeaderNavProvider>
            </LayoutProvider>
            </HeaderLayoutProvider>
            </PageTitleProvider>
        </ReactQueryProvider>
     /* </React.StrictMode>  */
)
